import { useState } from "react";
import { Heart, Calendar, IndianRupee, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import CopyLinkButton from "./CopyLinkButton";
import DonationSuccessModal from "./DonationSuccessModal";
import InternationalDonate from "./InternationalDonate";
import { siteUrl } from "../lib/siteConfig";

const ONE_TIME_TIERS = [500, 1000, 2500, 5000, 10000];
const DEFAULT_ONE_TIME_AMOUNT = 2500;
const MIN_DONATION_AMOUNT = 100;
const PAN_MAX_LENGTH = 10;

// Razorpay is now live. Toggle to `false` for a "coming soon" style CTA.
const RAZORPAY_LIVE = true;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function DonateSection({ defaultMode = "monthly", defaultRegion = "india" }) {
  const [mode, setMode] = useState(defaultMode); // "monthly" | "one-time"
  const [region, setRegion] = useState(defaultRegion); // "india" | "international"
  const [amount, setAmount] = useState(
    defaultMode === "monthly" ? 0 : DEFAULT_ONE_TIME_AMOUNT
  );
  const [customAmount, setCustomAmount] = useState("");
  const [donor, setDonor] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    pan: "",
    inspired_by: "",
    wants80g: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [successModal, setSuccessModal] = useState({
    open: false,
    mode: "one-time",
    amount: 0,
    email: "",
  });

  const tiers = ONE_TIME_TIERS;
  const effectiveAmount = customAmount ? Number(customAmount) : amount;

  // Validation rules per mode:
  //   monthly (Join the Circle) → everything is required
  //   one-time → name, email, address required; phone & PAN optional
  const isMonthly = mode === "monthly";
  const req = {
    name: true,
    email: true,
    address: true,
    phone: isMonthly,
    pan: isMonthly,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!donor.name || !donor.email || !donor.address) {
      toast.error("Please fill name, email and address.");
      return;
    }
    if (isMonthly && (!donor.phone || !donor.pan)) {
      toast.error("Phone and PAN are required to join the Circle.");
      return;
    }
    if (!effectiveAmount || effectiveAmount < 100) {
      toast.error("Minimum donation is ₹100.");
      return;
    }
    setSubmitting(true);
    try {
      if (!RAZORPAY_LIVE) {
        // Coming-soon fallback (kept for future toggling).
        toast.success("Thank you — we'll be in touch when donations open.");
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Couldn't load the payment gateway. Please refresh.");
        return;
      }

      const endpoint = isMonthly
        ? "/donate/create-subscription"
        : "/donate/create-order";
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: Number(effectiveAmount),
          donor: {
            name: donor.name,
            email: donor.email,
            phone: donor.phone,
            address: donor.address,
            pan: donor.pan,
            inspired_by: donor.inspired_by,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to start payment.");
      }
      const data = await res.json();

      const commonOptions = {
        key: data.razorpay_key_id,
        name: "Stories by Aradhana Foundation for Change",
        description: isMonthly
          ? "Foundation for Change Circle — monthly donation"
          : "Donation to the Foundation for Change",
        image: "/logo.png",
        prefill: {
          name: donor.name,
          email: donor.email,
          contact: donor.phone,
        },
        notes: { pan: donor.pan, address: donor.address },
        theme: { color: "#2d4a22" },
      };

      let options;
      if (isMonthly) {
        options = {
          ...commonOptions,
          subscription_id: data.subscription_id,
          handler: async (response) => {
            try {
              const v = await fetch(`${API}/donate/verify-subscription`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_subscription_id: response.razorpay_subscription_id,
                  razorpay_signature: response.razorpay_signature,
                  donation_id: data.donation_id,
                }),
              });
              if (!v.ok) throw new Error("Verification failed");
              const paidEmail = donor.email;
              const paidAmount = effectiveAmount;
              setSuccessModal({
                open: true,
                mode: "monthly",
                amount: paidAmount * 100,
                email: paidEmail,
              });
              setDonor({ name: "", email: "", phone: "", address: "", pan: "", inspired_by: "", wants80g: true });
              setAmount(0);
              setCustomAmount("");
            } catch {
              toast.error("Payment succeeded but verification is pending.");
            }
          },
        };
      } else {
        options = {
          ...commonOptions,
          amount: data.amount_paise,
          currency: data.currency,
          order_id: data.order_id,
          handler: async (response) => {
            try {
              const v = await fetch(`${API}/donate/verify`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  donation_id: data.donation_id,
                }),
              });
              if (!v.ok) throw new Error("Verification failed");
              const paidEmail = donor.email;
              const paidAmount = effectiveAmount;
              setSuccessModal({
                open: true,
                mode: "one-time",
                amount: paidAmount * 100,
                email: paidEmail,
              });
              setDonor({ name: "", email: "", phone: "", address: "", pan: "", inspired_by: "", wants80g: true });
              setAmount(DEFAULT_ONE_TIME_AMOUNT);
              setCustomAmount("");
            } catch {
              toast.error("Payment succeeded but verification is pending.");
            }
          },
        };
      }

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(
          resp?.error?.description ||
            "Payment failed. Please try again."
        );
      });
      rzp.open();
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="donate"
      data-testid="donate-section"
      className="bg-[#1a1a1a] text-[#fdfbf7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT — pitch (adapts to region) */}
          <div className="lg:col-span-5">
            {region === "international" ? (
              <>
                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
                  Become an International Patron
                </p>
                <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                  Some stories
                  <br />
                  don&rsquo;t end when
                  <br />
                  the <em className="italic">video</em> does.
                </h2>
                <p className="mt-8 font-sans text-[17px] leading-relaxed text-[#fdfbf7]/80 max-w-md">
                  Join our <strong className="font-semibold text-[#fdfbf7] underline underline-offset-4 decoration-[#f9d94b] decoration-2">creative community from abroad</strong> as a
                  Patron. Your paid membership sustains the storytelling —
                  documenting lives, journeys and moments across India — and
                  gives you exclusive content, impact updates and member-only
                  benefits every month.
                </p>

                <div className="mt-10 space-y-3 font-mono text-[12px] tracking-[0.15em] uppercase text-[#fdfbf7]/70">
                  <p className="flex items-center gap-3">
                    <ShieldCheck size={16} strokeWidth={1.4} className="text-[#f9d94b]" />
                    Paid digital membership · Not a charitable donation
                  </p>
                  <p className="flex items-center gap-3">
                    <Heart size={16} strokeWidth={1.4} className="text-[#f9d94b]" />
                    Cancel membership anytime from PayPal
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#f9d94b]">
                  Support the Foundation
                </p>
                <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                  Some stories
                  <br />
                  don&rsquo;t end when
                  <br />
                  the <em className="italic">video</em> does.
                </h2>
                <p className="mt-8 font-sans text-[17px] leading-relaxed text-[#fdfbf7]/80 max-w-md">
                  Join the{" "}
                  <a
                    href="#fcc"
                    data-testid="donate-fcc-anchor"
                    className="font-semibold text-[#fdfbf7] underline underline-offset-4 decoration-[#f9d94b] decoration-2 hover:decoration-[3px]"
                  >
                    Foundation for Change Circle (FCC)
                  </a>
                  {" "}— a small, close-knit community of monthly donors helping us
                  continue supporting the people whose stories we have shared, while
                  finding and documenting many more across India.
                </p>

                <div className="mt-10 space-y-3 font-mono text-[12px] tracking-[0.15em] uppercase text-[#fdfbf7]/70">
                  <p className="flex items-center gap-3">
                    <ShieldCheck size={16} strokeWidth={1.4} className="text-[#f9d94b]" />
                    Section 8 · Sections 332 &amp; 354 (erstwhile 12A &amp; 80G) Registered
                  </p>
                  <p className="flex items-center gap-3">
                    <Heart size={16} strokeWidth={1.4} className="text-[#f9d94b]" />
                    Cancel monthly anytime
                  </p>
                </div>
              </>
            )}
          </div>

          {/* RIGHT — donation form */}
          <div className="lg:col-span-7">
            {/* REGION TOGGLE — India vs International */}
            <div
              role="tablist"
              aria-label="Donation region"
              className="inline-flex border border-[#fdfbf7]/30 mb-8"
              data-testid="donate-region-toggle"
            >
              <button
                type="button"
                role="tab"
                aria-selected={region === "india"}
                onClick={() => setRegion("india")}
                data-testid="region-india"
                className={`px-6 py-3 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors ${
                  region === "india"
                    ? "bg-[#f9d94b] text-[#1a1a1a]"
                    : "text-[#fdfbf7]/70 hover:text-[#fdfbf7]"
                }`}
              >
                India
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={region === "international"}
                onClick={() => setRegion("international")}
                data-testid="region-international"
                className={`px-6 py-3 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors ${
                  region === "international"
                    ? "bg-[#f9d94b] text-[#1a1a1a]"
                    : "text-[#fdfbf7]/70 hover:text-[#fdfbf7]"
                }`}
              >
                International Patrons
              </button>
            </div>

            {region === "international" ? (
              <InternationalDonate defaultMode={mode} />
            ) : (
            <>
            {/* MODE TOGGLE */}
            <div
              role="tablist"
              aria-label="Donation type"
              className="inline-flex border border-[#fdfbf7]/30"
              data-testid="donate-mode-toggle"
            >
              <button
                type="button"
                role="tab"
                aria-selected={mode === "monthly"}
                data-testid="donate-mode-monthly"
                onClick={() => {
                  setMode("monthly");
                  setAmount(0);
                  setCustomAmount("");
                }}
                className={`px-5 md:px-7 py-3 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors flex items-center gap-2 ${
                  mode === "monthly"
                    ? "bg-[#f9d94b] text-[#1a1a1a]"
                    : "bg-transparent text-[#fdfbf7] hover:bg-[#fdfbf7]/10"
                }`}
              >
                <Calendar size={14} strokeWidth={1.4} />
                Join the Circle (FCC)
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "one-time"}
                data-testid="donate-mode-onetime"
                onClick={() => {
                  setMode("one-time");
                  setAmount(DEFAULT_ONE_TIME_AMOUNT);
                  setCustomAmount("");
                }}
                className={`px-5 md:px-7 py-3 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors flex items-center gap-2 border-l border-[#fdfbf7]/30 ${
                  mode === "one-time"
                    ? "bg-[#f9d94b] text-[#1a1a1a]"
                    : "bg-transparent text-[#fdfbf7] hover:bg-[#fdfbf7]/10"
                }`}
              >
                <IndianRupee size={14} strokeWidth={1.4} />
                One-time
              </button>
            </div>

            {/* Recurring info */}
            <div className="mt-6" />

            <form onSubmit={handleSubmit} className="mt-10 space-y-8">
              {/* AMOUNT TIERS */}
              <div>
                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#fdfbf7]/70 mb-4">
                  {mode === "monthly" ? "Choose your monthly amount" : "Donation amount"}
                </p>
                {mode === "monthly" ? (
                  <div className="flex flex-wrap gap-3 items-center" data-testid="donate-tiers">
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 font-serif text-xl text-[#fdfbf7]/70">
                        ₹
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="100"
                        required
                        placeholder="Enter amount"
                        data-testid="donate-custom-amount"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setAmount(0);
                        }}
                        className="w-56 pl-9 pr-4 py-3 bg-transparent border border-[#fdfbf7]/30 font-serif text-xl text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
                      />
                    </div>
                    <span className="font-mono text-[12px] tracking-[0.2em] uppercase text-[#fdfbf7]/55">
                      / month
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3" data-testid="donate-tiers">
                    {tiers.map((t) => {
                      const selected = !customAmount && amount === t;
                      return (
                        <button
                          key={t}
                          type="button"
                          data-testid={`donate-tier-${t}`}
                          onClick={() => {
                            setAmount(t);
                            setCustomAmount("");
                          }}
                          className={`px-5 py-3 font-serif text-lg transition-colors ${
                            selected
                              ? "bg-[#f9d94b] text-[#1a1a1a]"
                              : "bg-transparent text-[#fdfbf7] border border-[#fdfbf7]/30 hover:bg-[#fdfbf7]/10"
                          }`}
                        >
                          ₹{t.toLocaleString("en-IN")}
                        </button>
                      );
                    })}
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 font-serif text-lg text-[#fdfbf7]/60">
                        ₹
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={MIN_DONATION_AMOUNT}
                        placeholder="Custom"
                        data-testid="donate-custom-amount"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setAmount(0);
                        }}
                        className="w-32 pl-7 pr-3 py-3 bg-transparent border border-[#fdfbf7]/30 font-serif text-lg text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* DONOR DETAILS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input
                  type="text"
                  required={req.name}
                  placeholder={`Full name${req.name ? " *" : ""}`}
                  data-testid="donor-name"
                  value={donor.name}
                  onChange={(e) =>
                    setDonor((d) => ({ ...d, name: e.target.value }))
                  }
                  className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
                />
                <input
                  type="email"
                  required={req.email}
                  placeholder={`Email${req.email ? " *" : ""}`}
                  data-testid="donor-email"
                  value={donor.email}
                  onChange={(e) =>
                    setDonor((d) => ({ ...d, email: e.target.value }))
                  }
                  className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
                />
                <input
                  type="tel"
                  required={req.phone}
                  placeholder={`Phone${req.phone ? " *" : " (optional)"}`}
                  data-testid="donor-phone"
                  value={donor.phone}
                  onChange={(e) =>
                    setDonor((d) => ({ ...d, phone: e.target.value }))
                  }
                  className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
                />
                <div className="flex flex-col gap-1.5">
                  <input
                    type="text"
                    required={req.pan}
                    placeholder={req.pan ? "PAN *" : "PAN (optional)"}
                    data-testid="donor-pan"
                    value={donor.pan}
                    onChange={(e) =>
                      setDonor((d) => ({ ...d, pan: e.target.value.toUpperCase() }))
                    }
                    maxLength={PAN_MAX_LENGTH}
                    className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b] uppercase"
                  />
                  {!isMonthly && (donor.pan.trim() ? (
                    <p
                      data-testid="pan-helper-filled"
                      className="font-sans text-[11px] leading-snug text-[#f9d94b]/90"
                    >
                      ✅ Your Income Tax Exemption certificate will be shared as per the applicable provisions of the Income Tax Act.
                    </p>
                  ) : (
                    <p
                      data-testid="pan-helper-empty"
                      className="font-sans text-[11px] leading-snug text-[#fdfbf7]/55"
                    >
                      ⓘ Add your PAN if you&rsquo;d like an Income Tax Exemption certificate. Skip if you don&rsquo;t need one.
                    </p>
                  ))}
                </div>
                <textarea
                  required={req.address}
                  rows={2}
                  placeholder={`Postal address${req.address ? " *" : ""} (street, city, state, postal code)`}
                  data-testid="donor-address"
                  value={donor.address}
                  onChange={(e) =>
                    setDonor((d) => ({ ...d, address: e.target.value }))
                  }
                  className="md:col-span-2 bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b] resize-none"
                />
                <div className="md:col-span-2 flex flex-col gap-1.5">
                  <input
                    type="text"
                    placeholder="Which story inspired you to donate? (optional)"
                    data-testid="donor-inspired-by"
                    maxLength={140}
                    value={donor.inspired_by}
                    onChange={(e) =>
                      setDonor((d) => ({ ...d, inspired_by: e.target.value }))
                    }
                    className="bg-transparent border border-[#fdfbf7]/30 px-4 py-3 font-sans text-[15px] text-[#fdfbf7] placeholder:text-[#fdfbf7]/40 focus:outline-none focus:border-[#f9d94b]"
                  />
                  <p className="font-sans text-[11px] leading-snug text-[#fdfbf7]/55">
                    ⓘ Tell us which of our stories moved you — helps us know what&rsquo;s resonating.
                  </p>
                </div>
                <p className="md:col-span-2 font-mono text-[10px] tracking-[0.2em] uppercase text-[#fdfbf7]/45">
                  * Required {isMonthly ? "for joining the Circle" : "fields"}
                </p>
              </div>

              {/* Coming soon notice */}
              {!RAZORPAY_LIVE && (
                <div className="border-l-4 border-[#f9d94b] bg-[#f9d94b]/10 px-5 py-4">
                  <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#f9d94b]">
                    Donations launching August 2026
                  </p>
                  <p className="mt-2 font-sans text-sm text-[#fdfbf7]/80 leading-relaxed">
                    Our payment partner setup is in the final stages. Leave your
                    details below and we&rsquo;ll write to you the moment giving
                    opens — you&rsquo;ll be among the founding members of the
                    Circle.
                  </p>
                </div>
              )}

              {/* SUBMIT */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="donate-submit"
                  className="inline-flex items-center gap-3 bg-[#f9d94b] text-[#1a1a1a] px-8 py-4 text-sm font-sans tracking-[0.15em] uppercase hover:bg-[#fde277] transition-colors disabled:opacity-50"
                >
                  {submitting
                    ? "Submitting…"
                    : RAZORPAY_LIVE
                    ? mode === "monthly"
                      ? effectiveAmount
                        ? `Join the Circle · ₹${effectiveAmount.toLocaleString("en-IN")}/mo`
                        : "Join the Circle"
                      : `Donate ₹${effectiveAmount.toLocaleString("en-IN")}`
                    : mode === "monthly"
                    ? "Reserve my spot in the Circle"
                    : "Make my donation"}
                  <ArrowRight size={16} strokeWidth={1.4} />
                </button>
                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#fdfbf7]/50 ml-auto">
                  Secure · Razorpay
                </p>
              </div>

              {/* Shareable links — copy and forward */}
              <div className="pt-2">
                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#fdfbf7]/55 mb-3">
                  Share this with someone
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <CopyLinkButton
                    url={siteUrl("/join-the-circle")}
                    label="Copy monthly donor link"
                    variant="light"
                    testId="copy-monthly-link"
                  />
                  <CopyLinkButton
                    url={siteUrl("/donate-now")}
                    label="Copy one-time donate link"
                    variant="light"
                    testId="copy-onetime-link"
                  />
                </div>
              </div>
            </form>
            </>
            )}
          </div>
        </div>
      </div>

      <DonationSuccessModal
        open={successModal.open}
        onClose={() => setSuccessModal((s) => ({ ...s, open: false }))}
        mode={successModal.mode}
        amount={successModal.amount}
        email={successModal.email}
      />
    </section>
  );
}
